import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import {
  Home01Icon, Note01Icon, CheckmarkBadge01Icon, PackageSearchIcon, File01Icon, TaskDone01Icon,
  Comment01Icon, AiBrain01Icon, CheckmarkCircle01Icon, CheckmarkCircle02Icon, ShoppingCart01Icon,
  PackageReceiveIcon, Package01Icon, GridViewIcon, Analytics01Icon, Analytics02Icon, Analytics03Icon,
  ViewIcon, DeliveryTruck01Icon, DeliveryTruck02Icon, CreditCardIcon, CheckListIcon, DeliveryBox01Icon,
  DeliverySent01Icon, Invoice01Icon, Invoice02Icon, Invoice03Icon, Invoice04Icon, Ticket01Icon,
  ReturnRequestIcon, DeliveryReturn01Icon, ShoppingBag01Icon, ChartUpIcon, Exchange01Icon,
  ArrowDataTransferHorizontalIcon, ArrowDataTransferVerticalIcon, ArrowDataTransferDiagonalIcon,
  Store01Icon, ClipboardIcon, Book01Icon, Book02Icon, BarcodeIcon, MapsLocation01Icon, AnalyticsUpIcon,
  CubeIcon, SaleTag01Icon, Alert01Icon, ChartLineData01Icon, BalanceScaleIcon, PercentCircleIcon,
  PercentIcon, Building01Icon, Wallet01Icon, MoneyBag01Icon, Tag01Icon, UserGroupIcon, Coins01Icon,
  Location01Icon, Discount01Icon, Settings01Icon, Settings02Icon, UserMultiple02Icon, Shield01Icon,
  CircleIcon, HourglassIcon, PresentationBarChart01Icon,
  MoneyReceive01Icon, MoneySend01Icon, HierarchySquare02Icon, BookOpen01Icon, LeftToRightListNumberIcon, FileEditIcon, TaxesIcon, Building03Icon,
  Layers02Icon, Calculator01Icon, UserListIcon, IdIcon, MoneyBag02Icon, File02Icon, SlidersHorizontalIcon, DocumentCodeIcon, Certificate01Icon, CheckmarkSquare02Icon, AlertCircleIcon, Notification03Icon, Shield02Icon, ChartIncreaseIcon,
  ShoppingBasket01Icon, BarCode01Icon, PercentSquareIcon, SplitIcon, DiscountTag01Icon, ArrowUpRight02Icon, FileUploadIcon, Calendar02Icon, GitCompareIcon, AlertDiamondIcon, QrCodeIcon, TruckDeliveryIcon,
  Calendar03Icon, Money03Icon, BankIcon, ChartBarLineIcon, ArrowDownLeft01Icon, ArrowUpRight01Icon, MoneyRemove01Icon, MoneyAdd01Icon, Layers01Icon, PackageIcon, TableIcon,
} from '@hugeicons/core-free-icons';
import { SIDEBAR_GROUPS } from './modules';
import { useAuth } from '@/auth/useAuth';
import { useTheme } from './useTheme';
import { useIsMobile } from './useIsMobile';
import { getSettings } from '@/api/admin';
import logoDark from '@/assets/logo-dark.svg';
import logoLight from '@/assets/logo-light.svg';
import sidebarBg from '@/assets/sidebar-bg.png';

/* ── icon map: moduleId → Hugeicons icon (rendered via <HugeiconsIcon>) ── */
const MODULE_ICONS: Record<string, IconSvgElement> = {
  dashboard:            Home01Icon,
  // Purchase
  mrn:                  Note01Icon,
  hod:                  CheckmarkBadge01Icon,
  invcheck:             PackageSearchIcon,
  pr:                   File01Icon,
  'pr-approval':        TaskDone01Icon,
  rfq:                  Comment01Icon,
  ai:                   AiBrain01Icon,
  quoteapproval:        CheckmarkCircle01Icon,
  po:                   ShoppingCart01Icon,
  grn:                  PackageReceiveIcon,
  stock:                Package01Icon,
  // Sales
  catalogue:            GridViewIcon,
  'lead-dashboard':     Analytics02Icon,
  'enquiry-dashboard':  Analytics01Icon,
  'enq-review':         ViewIcon,
  'dispatch-order':     DeliveryTruck01Icon,
  'credit-check':       CreditCardIcon,
  'pick-list':          CheckListIcon,
  packing:              DeliveryBox01Icon,
  'outward-grn':        DeliverySent01Icon,
  'sp-invoice':         Invoice01Icon,
  'gate-pass':          Ticket01Icon,
  'sales-return':       ReturnRequestIcon,
  fulfillment:          CheckmarkCircle02Icon,
  // Ecommerce
  'online-orders':      ShoppingBag01Icon,
  shipping:             DeliveryTruck02Icon,
  'online-profitability': ChartUpIcon,
  'shopify-mapping':    Exchange01Icon,
  'shopify-stock':      ArrowDataTransferVerticalIcon,
  // Inventory
  'wh-stock':           Store01Icon,
  'wh-audit':           ClipboardIcon,
  'wh-ledger':          Book01Icon,
  'wh-serials':         BarcodeIcon,
  'wh-ageing':          HourglassIcon,
  'dept-transfer':      ArrowDataTransferDiagonalIcon,
  'sales-orders-field': MapsLocation01Icon,
  // SIS
  'cs-dashboard':       Analytics03Icon,
  'sis-analytics':      AnalyticsUpIcon,
  'sis-stock':          CubeIcon,
  'sis-transfer':       ArrowDataTransferHorizontalIcon,
  'sis-sales':          SaleTag01Icon,
  'sis-ledger':         Book02Icon,
  'sis-exceptions':     Alert01Icon,
  'sis-aging':          ChartLineData01Icon,
  'sis-settlement':     BalanceScaleIcon,
  'sis-cn-dn':          Invoice02Icon,
  'sis-profitability':  PercentCircleIcon,
  'sis-return-pool':    DeliveryReturn01Icon,
  'sis-assets':         Building01Icon,
  // Accounts
  'acc-invoice-logs':   Invoice02Icon,
  'rpt-meca':           PresentationBarChart01Icon,
  'acc-customer-os':    Wallet01Icon,
  'acc-supplier-os':    MoneyBag01Icon,
  'acc-sales-register': Invoice03Icon,
  'acc-purchase-register': Invoice04Icon,
  'acc-receipt':        MoneyReceive01Icon,
  'acc-payment':        MoneySend01Icon,
  'acc-contra':         ArrowDataTransferHorizontalIcon,
  'acc-journal':        Note01Icon,
  'acc-sales':          Invoice01Icon,
  'acc-purchase':       PackageReceiveIcon,
  'acc-notes':          ReturnRequestIcon,
  'acc-edit-log':       FileEditIcon,
  'acc-groups':         HierarchySquare02Icon,
  'acc-ledgers':        BookOpen01Icon,
  'acc-voucher-types':  LeftToRightListNumberIcon,
  'acc-tds':            TaxesIcon,
  'acc-cost-centres':   Building03Icon,
  'acc-day-book':       Calendar03Icon,
  'acc-cash-book':      Money03Icon,
  'acc-cash-bank-book': BankIcon,
  'acc-trial-balance':  TableIcon,
  'acc-profit-loss':    ChartBarLineIcon,
  'acc-balance-sheet':  BalanceScaleIcon,
  'acc-receivable':     ArrowDownLeft01Icon,
  'acc-payable':        ArrowUpRight01Icon,
  'acc-expense-report': MoneyRemove01Icon,
  'acc-income-report':  MoneyAdd01Icon,
  'acc-group-summary':  Layers01Icon,
  'acc-closing-stock':  PackageIcon,
  'acc-gst-sales': Invoice01Icon,
  'acc-gst-purchase': ShoppingBasket01Icon,
  'acc-gst-hsn': BarCode01Icon,
  'acc-gst-rate': PercentSquareIcon,
  'acc-gst-heads': SplitIcon,
  'acc-gst-itc': DiscountTag01Icon,
  'acc-gst-output': ArrowUpRight02Icon,
  'acc-gst-r1': FileUploadIcon,
  'acc-gst-3b': Calendar02Icon,
  'acc-gst-2b': GitCompareIcon,
  'acc-gst-portal': Exchange01Icon,
  'acc-gst-mismatch': AlertDiamondIcon,
  'acc-gst-einv': QrCodeIcon,
  'acc-gst-ewb': TruckDeliveryIcon,
  'acc-exp-entry': Invoice04Icon,
  'acc-exp-head': LeftToRightListNumberIcon,
  'acc-exp-month': Calendar03Icon,
  'acc-exp-dept': HierarchySquare02Icon,
  'acc-exp-budget': Calculator01Icon,
  'acc-exp-tds-prof': Certificate01Icon,
  'acc-exp-tds-con': DeliveryTruck02Icon,
  'acc-exp-tds-other': TaxesIcon,
  'acc-rec-brs': BankIcon,
  'acc-rec-gst': PercentSquareIcon,
  'acc-rec-2b': GitCompareIcon,
  'acc-rec-sales': ChartIncreaseIcon,
  'acc-rec-tds': Certificate01Icon,
  'acc-rec-invoice': Invoice02Icon,
  'acc-rec-purchase': ShoppingBasket01Icon,
  'acc-rec-party': UserListIcon,
  'acc-tds-deductions': Invoice03Icon,
  'acc-tds-sections': Layers02Icon,
  'acc-tds-rates': Calculator01Icon,
  'acc-tds-party': UserListIcon,
  'acc-tds-pan': IdIcon,
  'acc-tds-deducted': Coins01Icon,
  'acc-tds-payable': MoneyBag02Icon,
  'acc-tds-challans': File02Icon,
  'acc-tds-adjust': SlidersHorizontalIcon,
  'acc-tds-return': DocumentCodeIcon,
  'acc-tds-16a': Certificate01Icon,
  'acc-tds-rec': CheckmarkSquare02Icon,
  'acc-tds-outstanding': AlertCircleIcon,
  'acc-tds-due': Notification03Icon,
  'acc-tds-ldc': Shield02Icon,
  'acc-tds-threshold': ChartIncreaseIcon,
  // Masters
  items:                Tag01Icon,
  parties:              UserGroupIcon,
  'price-master':       Coins01Icon,
  locations:            Location01Icon,
  'sis-commission':     Discount01Icon,
  'hsn-gst-master':     PercentIcon,
  // System
  settings:             Settings01Icon,
  configurations:       Settings02Icon,
  usermgmt:             UserMultiple02Icon,
  rolemgmt:             Shield01Icon,
  // fallback handled in NavItem
};

/** Modules exclusive to the full RFQ chain — hidden when procurement mode is
 *  express-only, since the fast-track skips RFQ + Quote Approval. */
const FULL_ONLY_MODULES = new Set(['rfq', 'ai', 'quoteapproval']);

export function Sidebar() {
  const { hasPerm } = useAuth();
  const { sidebarCollapsed, toggleSidebar, theme } = useTheme();
  const isMobile = useIsMobile();
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings, staleTime: 60_000 });
  // Procurement mode governs which purchase modules are relevant. The express
  // fast-track (Approved PR → PO) skips the RFQ + Quote chain, so those modules
  // are hidden in express-only mode. Full and Both show everything.
  const mode = settings?.procurementMode ?? 'both';
  const [openGroupId, setOpenGroupId] = useState<string | null>(SIDEBAR_GROUPS[0]?.id ?? null);

  // On mobile: sidebar is always 240px wide, translated off-screen when collapsed
  // On desktop: sidebar shrinks to 64px icon-only when collapsed
  const effectiveCollapsed = isMobile ? false : sidebarCollapsed;

  const overlayGradient = theme === 'light'
    ? 'linear-gradient(160deg, rgba(255,251,240,0.38), rgba(235,218,188,0.48))'
    : 'linear-gradient(160deg, rgba(10,12,20,0.50), rgba(6,8,16,0.62))';

  return (
    <>
      {/* Mobile backdrop — tap to close */}
      {isMobile && !sidebarCollapsed && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
          onClick={toggleSidebar}
        />
      )}
    <nav
      className={clsx(
        'fixed left-0 top-0 z-40 flex h-screen flex-col overflow-hidden transition-[width,transform] duration-300',
        isMobile
          ? clsx('w-[210px]', sidebarCollapsed ? '-translate-x-full' : 'translate-x-0')
          : sidebarCollapsed ? 'w-16' : 'w-[240px]',
      )}
      style={{
        backgroundImage: `${overlayGradient}, url(${sidebarBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Logo + Toggle */}
      <div
        className="relative flex flex-shrink-0 h-[72px] items-center justify-center px-3"
        style={{ background: 'transparent' }}
      >
        {!effectiveCollapsed && (
          <img
            src={theme === 'dark' ? logoLight : logoDark}
            alt="grhamoy"
            className="h-[66px] w-auto max-w-[220px] object-contain"
          />
        )}
        {effectiveCollapsed && (
          <img
            src={theme === 'dark' ? logoLight : logoDark}
            alt="g"
            className="h-9 w-9 object-contain"
          />
        )}
      </div>

      {/* Items — scrollable, chromeless */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1 [scrollbar-width:none] md:py-2 [&::-webkit-scrollbar]:hidden">
        {SIDEBAR_GROUPS.map((group) => {
          const visibleItems = group.items.filter(
            // 'dashboard' is the home page — always visible, never permission-gated.
            (i) => (i.moduleId === 'dashboard' || hasPerm(i.moduleId)) && !(mode === 'express' && FULL_ONLY_MODULES.has(i.moduleId)),
          );
          if (visibleItems.length === 0) return null;
          const isOpen = openGroupId === group.id;
          return (
            <div key={group.id} className="mb-0.5 md:mb-1">
              {!effectiveCollapsed ? (
                <button
                  onClick={() => setOpenGroupId((s) => (s === group.id ? null : group.id))}
                  className="flex w-full items-center gap-1.5 px-3 py-1 text-left transition hover:text-[var(--text-primary)] md:gap-2 md:px-3.5 md:py-2"
                >
                  {/* Long section names (e.g. "Procurement Management") wrap rather than cut off. */}
                  <span className="flex-1 break-words text-[10px] font-extrabold uppercase leading-tight tracking-widest text-[var(--text-muted)] md:text-[13px]">
                    {group.label}
                  </span>
                  <svg
                    className={clsx('h-3 w-3 flex-shrink-0 text-[var(--text-muted)] transition md:h-3.5 md:w-3.5', isOpen && 'rotate-90')}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <div className="mx-2 my-1 h-px bg-[var(--border-color)] md:mx-3 md:my-1.5" />
              )}
              <div className={clsx('overflow-hidden transition-[max-height] duration-300', (isOpen || effectiveCollapsed) ? 'max-h-[600px]' : 'max-h-0')}>
                {visibleItems.map((item) => (
                  <NavItem key={item.moduleId} {...item} collapsed={effectiveCollapsed} onNavigate={isMobile && !sidebarCollapsed ? toggleSidebar : undefined} />
                ))}
              </div>
              {!effectiveCollapsed && <hr className="my-0.5 border-[var(--border-color)] md:my-1" />}
            </div>
          );
        })}
      </div>
    </nav>
    </>
  );
}

function NavItem({
  moduleId,
  path,
  label,
  collapsed,
  onNavigate,
}: {
  moduleId: string;
  path: string;
  label: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { hasPerm } = useAuth();
  if (moduleId !== 'dashboard' && !hasPerm(moduleId)) return null;

  const icon = (
    <HugeiconsIcon
      icon={MODULE_ICONS[moduleId] ?? CircleIcon}
      strokeWidth={1.8}
      className="h-[15px] w-[15px] md:h-[18px] md:w-[18px]"
    />
  );

  return (
    <NavLink
      to={path}
      end={path === '/'}
      title={label}
      onClick={onNavigate}
      className={({ isActive }) =>
        clsx(
          'mx-2 flex items-center gap-2 rounded-lg border-l-2 border-transparent py-1.5 pl-3 pr-2 text-[12px] font-bold text-[var(--text-primary)] transition md:gap-2.5 md:py-2 md:pl-4 md:pr-3 md:text-sm',
          isActive
            ? 'border-accent bg-accent !text-white shadow-sm hover:bg-accent'
            : 'hover:border-accent/40 hover:bg-accent/10',
          collapsed && 'justify-center !pl-0 !pr-0',
        )
      }
    >
      <span className={clsx('flex-shrink-0 opacity-70', collapsed ? 'opacity-100' : '')}>{icon}</span>
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
    </NavLink>
  );
}
